<script setup>
import {APIService} from '@/core/util/services/api.service';
import {required} from '@ohrm/core/util/validation/rules';
import {onMounted, reactive, ref, defineProps} from 'vue';
import OxdLabel from '@ohrm/oxd/core/components/Label/Label.vue';

// Before
const props = defineProps({
  id: {type: Number, default: null},
});

const isLoading = ref(false);

const rules = ref({
  code: [required],
  version: [required],
  questions: {
    title: [required],
  },
  status: [required],
});

const http = new APIService(
  window.appGlobal.baseUrl,
  `api/training/information`,
);

// Form
const form = reactive({
  id: props.id,
  answerId: null,
  code: '',
  version: '',
  description: '',
  questions: [],
  files: [],
  selectedOptions: [],
  haveComments: false,
  comment: '',
  answered: false,
});

onMounted(() => {
  isLoading.value = true;
  http
    .request({
      method: 'GET',
      url: `api/training/information/${props.id}`,
    })
    .then(response => {
      form.code = response.data.code;
      form.version = response.data.version;
      form.description = response.data.description;
      form.questions = response.data.questions;
      form.comment = response.data.comment;
      form.haveComments = response.data.haveComments;
      form.answered = response.data.answered;
      form.files = response.data.files;

      form.selectedOptions = response.data.questions.map(question => {
        let optionId = null;
        question.options.forEach(option => {
          if (option.selected) optionId = option.id;
        });

        return {
          id: question.id,
          option: optionId,
        };
      });

      if (!response.data.answered)
        http
          .request({
            method: 'POST',
            url: `api/training/information/answer/start`,
            data: {
              trainingInformationId: props.id,
            },
          })
          .then(response => {
            form.answerId = response.data;
          });
    })
    .finally(() => {
      isLoading.value = false;
    });
});

const structuredClone = object => {
  return JSON.parse(JSON.stringify(object));
};

const openFile = path => {
  window.open(`${window.appGlobal.publicPath}/${path}`, '_blank');
};

const onSave = () => {
  http
    .request({
      method: 'POST',
      url: 'api/training/information/answer',
      data: form,
    })
    .then(response => {
      window.location.assign(
        `${window.appGlobal.baseUrl}/training/viewInformation`,
      );
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const onCancel = () => {
  window.location.assign(
    `${window.appGlobal.baseUrl}/training/viewInformation`,
  );
};
</script>
<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        <template>
          Information
        </template>
      </oxd-text>
      <oxd-form :loading="isLoading" @submitValid.prevent="onSave">
        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.code"
                label="Title"
                placeholder="Title"
                :disabled="true"
                :rules="rules.code"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-input-field
            v-model="form.description"
            :disabled="true"
            type="textarea"
            label="Description"
            placeholder="Description"
          />
        </oxd-form-row>

        <oxd-form-row>
          <oxd-text tag="h6">
            Questions
          </oxd-text>
          <oxd-divider />

          <template
            v-for="(question, index) in form.questions"
            :key="question.id"
          >
            <oxd-grid-item>
              <oxd-input-group
                :classes="{wrapper: '--status-grouped-field'}"
                :label="question.value"
                required
              >
                <oxd-input-field
                  v-for="option in question.options"
                  :key="option.id"
                  v-model="form.selectedOptions[index].option"
                  :name="`radio_${question.id}`"
                  type="radio"
                  :disabled="form.answered"
                  :option-label="option.value"
                  :value="option.id"
                />
              </oxd-input-group>
            </oxd-grid-item>
          </template>
        </oxd-form-row>
        <oxd-divider />
        <br />
        <oxd-form-row>
          <oxd-text tag="h6">
            Files
          </oxd-text>
          <div
            v-for="file in form.files"
            :key="file.name"
            class="flex gap-3 items-center"
          >
            <oxd-icon-button name="download" @click="openFile(file.value)" />
            <OxdLabel :label="file.name" />
          </div>
        </oxd-form-row>
        <br />

        <oxd-form-row>
          <oxd-input-field
            v-if="form.haveComments"
            v-model="form.comment"
            :disabled="form.answered"
            type="textarea"
            label="Comments"
            placeholder="Comments"
          />
        </oxd-form-row>

        <oxd-divider />
        <oxd-form-actions class="gap-3">
          <required-text />
          <oxd-button
            display-type="ghost"
            :label="$t('general.cancel')"
            @click="onCancel"
          />
          <oxd-button
            v-if="form.answered"
            display-type="ghost"
            label="Answered"
          />
          <submit-button v-if="!form.answered" />
        </oxd-form-actions>
      </oxd-form>
    </div>
  </div>
</template>
<style scoped>
.w-100 {
  width: 100%;
}

.w-85 {
  width: 85%;
}

.w-20 {
  width: 20%;
}

.w-fit {
  width: fit-content;
}

.flex {
  display: flex;
}

.items-center {
  align-items: center;
}

.flex-column-direction {
  display: flex;
  flex-direction: column;
}

.gap-3 {
  gap: 3px;
}
.oxd-input-group__message {
  margin-top: 4px;
  font-size: 12px;
  font-weight: 600;
}
.oxd-text {
  font-family: 'Nunito Sans';
  margin-top: 0;
  margin-bottom: 0;
}
</style>
